import React, { useState, useMemo } from 'react';
import {
  VStack, HStack, Box, Text, Pressable, Icon, Button, ButtonText,
} from '@gluestack-ui/themed';
import {
  ChevronDown, ChevronUp, Eye, RefreshCw,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { QuestionCard } from '@/student/task/composer/MCQComposer/QuestionCard';
import type { MCQQuestion } from '@/common/api/models';

interface MCQConfigSummary {
  questions: MCQQuestion[];
  passThresholdPercent?: number;
  shuffleQuestions?: boolean;
  shuffleOptions?: boolean;
  timeLimitMinutes?: number | null;
  questionsToShow?: number | null;
}

interface MCQPreviewPanelProps {
  mcqConfig: MCQConfigSummary;
}

/**
 * MCQPreviewPanel
 * ---------------
 * Lecturer-only collapsible panel that shows MCQ quiz questions
 * in read-only mode during module preview. Lets the lecturer verify
 * question content without leaving the event task context.
 */
const MCQPreviewPanel: React.FC<MCQPreviewPanelProps> = ({ mcqConfig }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [sampleSeed, setSampleSeed] = useState(0);
  const { questions } = mcqConfig;

  // Derive questions deterministically based on sampleSeed if questionsToShow is active
  const displayedQuestions = useMemo(() => {
    if (!questions || questions.length === 0) return [];

    let result = [...questions];

    // Only apply shuffling and sampling if configured
    if (mcqConfig.questionsToShow && mcqConfig.questionsToShow < questions.length) {
      // Very simple deterministic shuffle for UI preview purposes only (not crypto secure)
      if (mcqConfig.shuffleQuestions) {
        // A simple seeded PRNG for visual preview
        const random = (seedArg: number) => {
          const x = Math.sin(seedArg) * 10000;
          return x - Math.floor(x);
        };

        let currentSeed = sampleSeed;
        for (let i = result.length - 1; i > 0; i -= 1) {
          const j = Math.floor(random(currentSeed) * (i + 1));
          currentSeed += 1;
          [result[i], result[j]] = [result[j], result[i]];
        }
      }

      result = result.slice(0, mcqConfig.questionsToShow);
    }

    return result;
  }, [questions, mcqConfig.questionsToShow, mcqConfig.shuffleQuestions, sampleSeed]);

  if (!questions || questions.length === 0) return null;

  const configParts: string[] = [];
  if (mcqConfig.passThresholdPercent != null) {
    configParts.push(t('lecturer.preview.mcq.passThreshold', { percent: mcqConfig.passThresholdPercent }));
  }
  if (mcqConfig.timeLimitMinutes) {
    configParts.push(t('lecturer.preview.mcq.timeLimit', { minutes: mcqConfig.timeLimitMinutes }));
  }
  if (mcqConfig.shuffleQuestions) {
    configParts.push(t('lecturer.preview.mcq.shuffled'));
  }

  const isSampling = mcqConfig.questionsToShow && mcqConfig.questionsToShow < questions.length;
  if (isSampling) {
    configParts.push(t('lecturer.preview.mcq.questionsSubset', {
      shown: mcqConfig.questionsToShow,
      total: questions.length,
    }));
  }

  const noopSelect = () => {};

  return (
    <VStack
      testID="mcq-preview-panel"
      space="sm"
      mt="$3"
      p="$3"
      bg="$backgroundLight50"
      borderRadius="$md"
      borderWidth={1}
      borderStyle="dashed"
      borderColor="$borderLight300"
    >
      <HStack alignItems="center" justifyContent="space-between">
        <HStack space="sm" alignItems="center">
          <Icon as={Eye} size="sm" color="$textSubtle" />
          <Text size="xs" color="$textSubtle" fontWeight="semibold">
            {t('lecturer.preview.mcq.label')}
          </Text>
        </HStack>
        {configParts.length > 0 && (
          <Text size="xs" color="$textHint">
            {configParts.join(' · ')}
          </Text>
        )}
      </HStack>

      <Pressable
        testID="mcq-preview-toggle"
        onPress={() => setExpanded((prev) => !prev)}
      >
        <HStack
          space="sm"
          alignItems="center"
          py="$2"
          px="$3"
          bg="$bgCard"
          borderRadius="$sm"
          borderWidth={1}
          borderColor="$borderLight200"
        >
          <Text size="sm" color="$textBody" fontWeight="medium" flex={1}>
            {t('lecturer.preview.mcq.toggleButton', { count: displayedQuestions.length })}
          </Text>
          <Icon as={expanded ? ChevronUp : ChevronDown} size="sm" color="$textSubtle" />
        </HStack>
      </Pressable>

      {expanded && (
        <Box testID="mcq-preview-questions" mt="$2">
          {!!isSampling && (
            <Box mb="$3" alignItems="flex-end">
              <Button
                size="xs"
                variant="outline"
                action="secondary"
                onPress={() => setSampleSeed((s) => s + 1)}
                testID="mcq-preview-resample"
              >
                <Icon as={RefreshCw} size="xs" mr="$1" />
                <ButtonText>{t('lecturer.preview.mcq.resampleQuestions')}</ButtonText>
              </Button>
            </Box>
          )}
          {displayedQuestions.map((question, index) => (
            <QuestionCard
              key={question.id}
              question={question as unknown as Parameters<typeof QuestionCard>[0]['question']}
              questionNumber={index + 1}
              onSelectAnswer={noopSelect}
              showResults={false}
              isDisabled
            />
          ))}
        </Box>
      )}
    </VStack>
  );
};

export default MCQPreviewPanel;
